import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEstimate } from "../store/EstimateContext";
import { fmtCurrency, fmtHours } from "../lib/calculations";
import { Briefcase, BarChart3, Users, Tag, FolderOpen } from "lucide-react";

import HelpIconButton from "./HelpIconButton";
const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

function aggregate(savedList) {
  // Per-currency totals
  const byCurrency = {};
  // Role hours aggregated
  const rolesAgg = {};
  // Tag distribution
  const tags = {};
  // Top projects array {name, currency, grandTotal, hours}
  const projects = [];

  for (const e of savedList) {
    const code = e.currency?.code || "USD";
    const sym = e.currency?.symbol || "$";
    if (!byCurrency[code]) {
      byCurrency[code] = { code, symbol: sym, grandTotal: 0, baseCost: 0, hours: 0, projectCount: 0, dms: 0, illus: 0 };
    }
    const totals = e.totals || {};
    const grand = n(totals.grandTotal ?? totals.totalCost);
    const base = n(totals.baseCost);
    const hrs = n(totals.totalHours);
    byCurrency[code].grandTotal += grand;
    byCurrency[code].baseCost += base;
    byCurrency[code].hours += hrs;
    byCurrency[code].projectCount += 1;
    byCurrency[code].dms += n(totals.totalDms);
    byCurrency[code].illus += n(totals.totalIllus);

    // Role hours = effortPercent/100 * totalHours
    for (const r of (e.roles || [])) {
      const fraction = n(r.effortPercent) / 100;
      const roleHours = hrs * fraction;
      const key = r.name || "Unnamed";
      if (!rolesAgg[key]) rolesAgg[key] = { name: key, hours: 0, projectCount: 0 };
      rolesAgg[key].hours += roleHours;
      rolesAgg[key].projectCount += 1;
    }

    for (const t of (e.tags || [])) {
      tags[t] = (tags[t] || 0) + 1;
    }

    projects.push({
      id: e.id,
      name: e.projectName || "Untitled",
      currency: code,
      symbol: sym,
      grandTotal: grand,
      hours: hrs,
      dms: n(totals.totalDms),
      tags: e.tags || [],
      updatedAt: e.updatedAt,
    });
  }

  const currencyRows = Object.values(byCurrency).sort((a, b) => b.grandTotal - a.grandTotal);
  const totalProjects = savedList.length;
  const totalHours = currencyRows.reduce((s, c) => s + c.hours, 0);
  const totalDms = currencyRows.reduce((s, c) => s + c.dms, 0);
  const roleRows = Object.values(rolesAgg).sort((a, b) => b.hours - a.hours);
  const totalRoleHours = roleRows.reduce((s, r) => s + r.hours, 0) || 1;
  const roleWithShare = roleRows.map((r) => ({ ...r, sharePercent: (r.hours / totalRoleHours) * 100 }));
  const tagRows = Object.entries(tags).map(([t, c]) => ({ tag: t, count: c })).sort((a, b) => b.count - a.count);

  return {
    currencyRows, roleRows: roleWithShare, tagRows,
    totalProjects, totalHours, totalDms, projects,
  };
}

export default function PortfolioTab() {
  const { t } = useTranslation();
  const { savedList, loadEstimate } = useEstimate();
  const [filterCurrency, setFilterCurrency] = useState("all");

  const agg = useMemo(() => aggregate(savedList), [savedList]);

  const filteredProjects = useMemo(() => {
    const all = [...agg.projects].sort((a, b) => b.grandTotal - a.grandTotal);
    if (filterCurrency === "all") return all.slice(0, 10);
    return all.filter((p) => p.currency === filterCurrency).slice(0, 10);
  }, [agg.projects, filterCurrency]);

  return (
    <section className="space-y-6" data-testid="portfolio-tab">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">
            {t("portfolio.section")}
          </p>
          <h2 className="font-heading text-2xl tracking-tight font-bold text-black flex items-center gap-2">
            {t("portfolio.title")}
            <HelpIconButton anchor="ch5" testId="help-icon-portfolio" />
          </h2>
          <p className="text-sm text-gray-600 mt-1 max-w-2xl">
            {t("portfolio.description")}
          </p>
        </div>
        <div className="grid grid-cols-3 gap-3" data-testid="portfolio-headline">
          <Stat label="Saved Projects" value={agg.totalProjects} />
          <Stat label="Total Hours" value={fmtHours(agg.totalHours)} />
          <Stat label="Total DMs" value={agg.totalDms} />
        </div>
      </div>

      {agg.totalProjects === 0 ? (
        <div className="bg-white border border-gray-300 p-12 text-center" data-testid="portfolio-empty">
          <FolderOpen size={32} className="mx-auto text-gray-300" />
          <p className="text-gray-500 text-sm mt-3">No saved estimates yet — save a project to populate the portfolio.</p>
        </div>
      ) : (
        <>
          {/* Currency breakdown */}
          <div className="bg-white border border-gray-300" data-testid="portfolio-currency-table">
            <div className="border-b border-gray-300 p-4 flex items-center gap-2">
              <BarChart3 size={16} className="text-[#002FA7]" />
              <h3 className="font-heading text-lg tracking-tight font-bold text-black">Revenue by Currency</h3>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-100 text-black font-bold uppercase tracking-wider text-[10px] border-b border-gray-300">
                  <th className="py-2 px-3 text-left">Currency</th>
                  <th className="py-2 px-3 text-right">Projects</th>
                  <th className="py-2 px-3 text-right">Hours</th>
                  <th className="py-2 px-3 text-right">DMs</th>
                  <th className="py-2 px-3 text-right">Base</th>
                  <th className="py-2 px-3 text-right">Grand Total</th>
                </tr>
              </thead>
              <tbody>
                {agg.currencyRows.map((c) => (
                  <tr key={c.code} className="border-b border-gray-200" data-testid={`portfolio-currency-${c.code}`}>
                    <td className="py-2 px-3 font-bold">{c.symbol} {c.code}</td>
                    <td className="py-2 px-3 font-mono text-right">{c.projectCount}</td>
                    <td className="py-2 px-3 font-mono text-right">{fmtHours(c.hours)}</td>
                    <td className="py-2 px-3 font-mono text-right">{c.dms}</td>
                    <td className="py-2 px-3 font-mono text-right text-gray-500">{fmtCurrency(c.baseCost, c.symbol)}</td>
                    <td className="py-2 px-3 font-mono text-right font-bold text-[#002FA7]">{fmtCurrency(c.grandTotal, c.symbol)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Roles and Tags grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-gray-300" data-testid="portfolio-roles">
              <div className="border-b border-gray-300 p-4 flex items-center gap-2">
                <Users size={16} className="text-[#002FA7]" />
                <h3 className="font-heading text-lg tracking-tight font-bold text-black">Role Utilisation</h3>
                <span className="text-xs text-gray-500 font-mono ml-2">{fmtHours(agg.roleRows.reduce((s, r) => s + r.hours, 0))} hrs across all projects</span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100 text-black font-bold uppercase tracking-wider text-[10px] border-b border-gray-300">
                    <th className="py-2 px-3 text-left">Role</th>
                    <th className="py-2 px-3 text-right">Hours</th>
                    <th className="py-2 px-3 text-right">% Share</th>
                    <th className="py-2 px-3 text-left">Bar</th>
                  </tr>
                </thead>
                <tbody>
                  {agg.roleRows.length === 0 ? (
                    <tr><td colSpan={4} className="py-6 text-center text-gray-400 text-xs">No role data.</td></tr>
                  ) : agg.roleRows.map((r) => (
                    <tr key={r.name} className="border-b border-gray-200" data-testid={`portfolio-role-${r.name.replace(/\s+/g, "-")}`}>
                      <td className="py-2 px-3 font-semibold">{r.name}</td>
                      <td className="py-2 px-3 font-mono text-right">{fmtHours(r.hours)}</td>
                      <td className="py-2 px-3 font-mono text-right">{r.sharePercent.toFixed(1)}%</td>
                      <td className="py-2 px-3">
                        <div className="w-full bg-gray-100 h-2">
                          <div className="h-2 bg-[#002FA7]" style={{ width: `${Math.min(100, r.sharePercent)}%` }} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="bg-white border border-gray-300" data-testid="portfolio-tags">
              <div className="border-b border-gray-300 p-4 flex items-center gap-2">
                <Tag size={16} className="text-[#FF6B00]" />
                <h3 className="font-heading text-lg tracking-tight font-bold text-black">Tag Distribution</h3>
              </div>
              <div className="p-4">
                {agg.tagRows.length === 0 ? (
                  <p className="text-xs text-gray-400" data-testid="portfolio-tags-empty">No tags on saved estimates yet.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {agg.tagRows.map((t) => (
                      <span
                        key={t.tag}
                        data-testid={`portfolio-tag-${t.tag}`}
                        className="inline-flex items-center gap-2 bg-black text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1"
                      >
                        {t.tag}
                        <span className="bg-white text-black px-1 text-[9px] font-mono">{t.count}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Top projects */}
          <div className="bg-white border border-gray-300" data-testid="portfolio-top-projects">
            <div className="border-b border-gray-300 p-4 flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Briefcase size={16} className="text-[#002FA7]" />
                <h3 className="font-heading text-lg tracking-tight font-bold text-black">Top Projects by Value</h3>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">Currency</label>
                <select
                  value={filterCurrency}
                  onChange={(e) => setFilterCurrency(e.target.value)}
                  data-testid="portfolio-currency-filter"
                  className="border border-gray-300 rounded-none px-2 py-1 font-mono text-xs bg-white"
                >
                  <option value="all">All</option>
                  {agg.currencyRows.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
                </select>
              </div>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-100 text-black font-bold uppercase tracking-wider text-[10px] border-b border-gray-300">
                  <th className="py-2 px-3 text-left">Project</th>
                  <th className="py-2 px-3 text-right">DMs</th>
                  <th className="py-2 px-3 text-right">Hours</th>
                  <th className="py-2 px-3 text-right">Grand Total</th>
                  <th className="py-2 px-3 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.length === 0 ? (
                  <tr><td colSpan={5} className="py-6 text-center text-gray-400 text-xs">No projects match the filter.</td></tr>
                ) : filteredProjects.map((p) => (
                  <tr key={p.id} className="border-b border-gray-200" data-testid={`portfolio-project-row-${p.id}`}>
                    <td className="py-2 px-3">
                      <p className="font-semibold">{p.name}</p>
                      <div className="flex gap-1 flex-wrap mt-1">
                        {p.tags.slice(0, 4).map((t) => (
                          <span key={t} className="text-[9px] uppercase tracking-wider bg-gray-100 text-gray-600 px-1 py-0.5 font-bold">{t}</span>
                        ))}
                      </div>
                    </td>
                    <td className="py-2 px-3 font-mono text-right">{p.dms}</td>
                    <td className="py-2 px-3 font-mono text-right">{fmtHours(p.hours)}</td>
                    <td className="py-2 px-3 font-mono text-right font-bold text-[#002FA7]">{fmtCurrency(p.grandTotal, p.symbol)}</td>
                    <td className="py-2 px-3 text-right">
                      <button
                        onClick={() => loadEstimate(p.id)}
                        data-testid={`portfolio-open-${p.id}`}
                        className="bg-white border border-gray-300 hover:bg-gray-100 rounded-none px-2 py-1 text-xs font-bold inline-flex items-center gap-1"
                      >
                        <FolderOpen size={12} /> Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-gray-300 px-3 py-2 bg-white min-w-[130px]">
      <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">{label}</p>
      <p className="font-mono text-xl font-bold mt-1 leading-none">{value}</p>
    </div>
  );
}
